import { Button } from "@bindery/ui/components/ui/button";
import { Card, CardTitle } from "@bindery/ui/components/ui/card";
import { Progress } from "@bindery/ui/components/ui/progress";
import { BookOpen, Star } from "lucide-react";
import { Link } from "react-router";
import type { BookmarkValue } from "@/storage/db";
import { getBookById } from "./default-library";
import { timeAgo } from "./time-ago";

interface BookmarkCardProps {
  bookmark: BookmarkValue;
  totalPages?: number;
  showProgress?: boolean;
}

function getBookmarkUrls(readingId: string, completed: boolean) {
  const continueUrl = `/reader/${readingId}`;
  const startOverUrl = `/reader/${readingId}?page=0`;
  const primaryUrl = completed ? startOverUrl : continueUrl;
  return { continueUrl, startOverUrl, primaryUrl };
}

export function BookmarkCard({
  bookmark,
  totalPages,
  showProgress = true,
}: BookmarkCardProps) {
  const bookData = getBookById(bookmark.readingId);

  const completed = bookmark.completed ?? false;
  const { startOverUrl, primaryUrl } = getBookmarkUrls(
    bookmark.readingId,
    completed,
  );

  if (!bookData) {
    return (
      <Card className="bg-muted p-4">
        <p className="text-muted-foreground text-sm">
          Book not found (ID: {bookmark.readingId})
        </p>
      </Card>
    );
  }

  const progress = totalPages
    ? Math.round((bookmark.currentPage / totalPages) * 100)
    : 0;

  return (
    <Card className="group overflow-hidden transition-shadow hover:shadow-lg">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto_1fr]">
        <Link to={primaryUrl}>
          <div className="bg-muted relative aspect-[3/4] w-full sm:w-32">
            <img
              src={bookData.imageURL}
              alt={bookData.title}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
            />
            {bookmark.completed && (
              <div className="bg-mintleaf-600 absolute top-2 right-2 rounded-full p-2 text-white">
                <BookOpen className="h-4 w-4" />
              </div>
            )}
          </div>
        </Link>

        <div className="flex flex-col justify-between p-4 sm:p-6">
          <div>
            <Link to={primaryUrl}>
              <CardTitle className="mb-2 line-clamp-2 text-lg hover:underline">
                {bookData.title}
              </CardTitle>
            </Link>
            <p className="text-muted-foreground mb-3 text-sm">
              {bookData.byline}
            </p>

            {showProgress && totalPages ? (
              <div className="mb-3">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    Page {bookmark.currentPage + 1} of {totalPages}
                  </span>
                  <span className="text-skyfall-600 font-medium">
                    {progress}%
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            ) : null}

            {bookmark.stars ? (
              <div className="mb-2 flex items-center gap-1">
                {Array.from({ length: bookmark.stars }, (_, i) => (
                  <Star
                    key={`star-${i}`}
                    className="text-flamenco-500 h-4 w-4 fill-current"
                  />
                ))}
              </div>
            ) : null}

            <p className="text-muted-foreground text-xs">
              {bookmark.completed ? "Completed" : "Last read"}{" "}
              {timeAgo(bookmark.lastupdate)}
            </p>
          </div>

          <div className="mt-4 flex gap-2">
            <Button
              asChild
              className="bg-skyfall-600 hover:bg-skyfall-700 flex-1"
              size="sm"
            >
              <Link to={primaryUrl}>
                {bookmark.completed ? "Read Again" : "Continue Reading"}
              </Link>
            </Button>
            {!bookmark.completed && bookmark.currentPage > 0 && (
              <Button asChild variant="outline" size="sm">
                <Link to={startOverUrl}>Start Over</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
